/* @flow */
import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { css, merge } from 'glamor';

import { trackVideoPlay } from 'depop/utils/tracking';

import { GREY_C } from 'depop/styles/colors';
import { QUERY_MOBILE } from 'depop/styles/layout';

import VerticallyCentered from 'depop/components/VerticallyCentered';
import HLSSource from 'depop/components/videos/HLSSource';
import PosterImage from 'depop/components/videos/PosterImage';

import VideoLoader from 'depop/components/icons/Spinner';
import VideoVolumeOn from 'depop/components/icons/VideoVolumeOn';
import VideoVolumeOff from 'depop/components/icons/VideoVolumeOff';
import VideoPlay from 'depop/components/icons/VideoPlay';
import VideoBroken from 'depop/components/icons/VideoBroken';

const VOLUME_BUTTON_Z_INDEX = '500';
const PLAY_BUTTON_Z_INDEX = '400';
const POSTER_IMAGE_Z_INDEX = '300';
const VIDEO_Z_INDEX = '100';

const buttonStyle = {
  cursor: 'pointer',
  userSelect: 'none',
  outline: 'none'
};

const playButtonStyle = css({
  ...buttonStyle,
  background: 'transparent',
  border: 'none',
  transition: 'all 0.4s',
  width: '100%',
  height: '100%',
  position: 'absolute',
  zIndex: PLAY_BUTTON_Z_INDEX,
  top: '0',
  right: '0',
  bottom: '0',
  left: '0',
  textAlign: 'center',
  verticalAlign: 'middle',
  display: 'block'
});

const soundToggleButtonStyle = css({
  ...buttonStyle,
  background: 'transparent',
  border: 'none',
  position: 'absolute',
  top: '2.5%',
  left: '5%',
  zIndex: VOLUME_BUTTON_Z_INDEX,
  padding: '0.3em 0.2em 0.1em',
  transition: 'all 0.4s',
  WebkitTapHighlightColor: 'transparent'
});

const toggleIconStyle = css({
  display: 'inline-block',
  maxWidth:  '6rem',
  maxHeight: '6rem',
  width: '100%',
  height: 'auto',
  '@media (max-width: 701px)': {
    maxWidth:  '5rem',
    maxHeight: '5rem',
  },
});

const posterStyle = css({
  width: '100%',
  height: '100%',
  position: 'absolute',
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  zIndex: POSTER_IMAGE_Z_INDEX,
  transition: 'all 0.4s'
});

const videoCntStyle = css({
  position: 'relative',
  paddingTop: '100%',
  backgroundColor: GREY_C,
  ':hover': {
    '> .video-volume-btn': {
      opacity: 1
    },
    '> .video-no-sound': {
      opacity: 1
    }
  }
});

const videoStyle = css({
  width: '100%',
  height: '100%',
  position: 'absolute',
  zIndex: VIDEO_Z_INDEX,
  top: 0,
  right: 0,
  bottom: 0,
  left: 0
});

const rotate = css.keyframes('rotate', {
  'from': { transform: 'rotate(0deg)'},
    'to': { transform: 'rotate(360deg)'}
});

const slidein = css.keyframes('slidein', {
   '0%, 100%': { transform: 'translate3d(-20%,0,0)', opacity: 0 },
   '5%,  95%': {                                     opacity: 0 },
  '10%,  90%': { transform: 'translate3d(  0%,0,0)', opacity: 1 }
});

const soundVisibilityStyle = css({
  opacity: 0.6,
  ':active': { opacity: 0.9 },
  '@media (max-width: 701px)': {
    opacity: 1,
    ':hover':  { opacity: 1 },
    ':active': { opacity: 1 }
  }
});

const videoNoSoundWrapperStyle = css({
  position: 'absolute',
  top: '2.5%',
  left:  '5%',
  right: '5%',
  overflow: 'hidden',
  margin: '21px 22px 0',
  padding: '0 1.8em',
  zIndex: '400',
  color: 'white',
  lineHeight: '1em',
  transition: 'all 0.4s',
  opacity: 0.6,
  '@media (max-width: 701px)': {
    opacity: 1
  }
});

const videoNoSoundTextStyle = css({
  display: 'block',
  transform: 'translate3d(-100%,0,0)',
  opacity: 0,
  textShadow: '1px 1px 1px rgba(0,0,0,0.5)',
  animation: `${slidein}`,
  animationIterationCount: 1,
  animationDuration: '6s',
  animationDelay: '2s',
  animationTimingFunction: 'ease-in-out'
});

const videoSpinnerStyle = css({
  position: 'absolute',
  opacity: 1,
  zIndex: PLAY_BUTTON_Z_INDEX,
  top: '50%',
  left: '50%',
  marginTop: '-25px',
  marginLeft: '-25px',
  width: '50px',
  height: '50px',
});

const fullVisibilityStyle = css({ opacity: 1 });


class VideoPlayer extends Component {
  props: {
    videoURL:     PropTypes.string.isRequired,
    thumbnailURL: PropTypes.string,
    width:        PropTypes.number,
    height:       PropTypes.number,
    fluid:        PropTypes.bool,
    autoPlay:     PropTypes.bool,
    playsInline:  PropTypes.bool,
  };

  constructor(props) {
    super(props);

    this.video = null; // the html5 video
    this.play = this.play.bind(this);
    this.pause = this.pause.bind(this);
    this.togglePlaying = this.togglePlaying.bind(this);
    this.toggleSound = this.toggleSound.bind(this);
    this.handlePlaying = this.handlePlaying.bind(this);
    this.handlePause = this.handlePause.bind(this);
    this.handleVolumeChange = this.handleVolumeChange.bind(this);
    this.handleError = this.handleError.bind(this);
    this.handleSuspend = this.handleSuspend.bind(this);
    this.handleAborted = this.handleAborted.bind(this);
    this.handleEmptied = this.handleEmptied.bind(this);
    this.handleStalled = this.handleStalled.bind(this);
    this.handleCanPlay = this.handleCanPlay.bind(this);
    this.handleWaiting = this.handleWaiting.bind(this);
    this.handleSeeking = this.handleSeeking.bind(this);
    this.handleSeeked = this.handleSeeked.bind(this);
    this.handleLoadStart = this.handleLoadStart.bind(this);
    this.handleLoadedData = this.handleLoadedData.bind(this);
    this.handleSourceParsed = this.handleSourceParsed.bind(this);
    this.handleSourceError = this.handleSourceError.bind(this);

    this.state = {
      loading: true,
      playing: false,
      muted: false,
      broken: false,
      started: false,
      noSound: true
    };
  }

  componentDidMount() {
    this.forceUpdate(); // make sure the children can get the video property
  }

  componentWillMount() {
    this.pause();
  }

  pause () {
    if (!this.video) {
      return false;
    }
    this.video.pause();
  }

  play () {
    if (!this.video) {
      return false;
    }
    this.video.play();
    trackVideoPlay();
  }

  mute () {
    if (!this.video) {
      return false;
    }
    this.video.muted = true;
  }

  unMute () {
    if (!this.video) {
      return false;
    }
    this.video.muted = false;
  }

  togglePlaying () {
    if (this.state.playing) {
      this.pause();
    } else {
      this.play();
    }
  }

  toggleSound () {
    if (this.state.muted) {
      this.unMute();
    } else {
      this.mute();
    }
  }

  handlePlaying () {
    this.setState({
      playing: true,
      started: true
    });
  }

  handlePause () {
    this.setState({ playing: false });
  }

  handleVolumeChange () {
    if (!this.video) {
      return false;
    }

    if (this.video.muted) {
      this.setState({ muted: true });
    } else {
      this.setState({ muted: false });
    }
  }

  handleError () {
    // console.log('handleError');
    this.setState({ broken: true });
  }

  handleSuspend () {
    // console.log('handleSuspend');
    // this.setState({ broken: true });
  }

  handleAborted () {
    console.log('handleAborted');
    // this.setState({ broken: true });
  }

  handleEmptied () {
    // console.log('handleEmptied');
    // this.setState({ broken: true });
  }

  handleStalled () {
    // console.log('handleStalled');
    // this.setState({ broken: true });
  }

  handleCanPlay () {
    console.log('handleCanPlay');

    if (!this.video || !this.state.loading) {
      return false;
    }

    this.video.load();
  }

  handleLoadStart () {
    console.log('handleLoadStart');
  }

  handleLoadedData () {
    console.log('handleLoadedData');

    if (!this.video) {
      return false;
    }

    this.setState({
      'loading': false,
      'noSound': !this.hasAudio()
    });
  }

  handleWaiting () {
    console.log('handleWaiting');
  }

  handleSeeking () {
    console.log('handleSeeking');
  }

  handleSeeked () {
    console.log('handleSeeked');
  }

  handleSourceParsed (data) {
    this.setState({
      'loading': false,
      'noSound': !data.hasSound
    });
    console.log('handleSourceParsed: ', data);
  }

  handleSourceError (data) {
    this.pause();
    this.setState({
      'loading': false,
      'broken': true
    });
    console.log('handleSourceError: ', data);
  }

  componentWillUpdate (nextProps) {
    const { media = {} } = nextProps;
    const { carouselIndex, carouselMoving } = media;

    // Pause video when user navigates to another carousel slide
    // if (carouselIndex || carouselMoving) {
    if (carouselIndex) {
      this.pause();
    }
  }

  hasAudio () {
    if (!this.video) {
      return false;
    }

    return this.video.mozHasAudio ||
           Boolean(this.video.webkitAudioDecodedByteCount) ||
           Boolean(this.video.audioTracks && this.video.audioTracks.length);
  }

  render () {
    const {
      videoURL = '',
      thumbnailURL = '',
      width = 300,
      height = 300,
      fluid = false,
      autoPlay = false,
      playsInline = true,
    } = this.props;
    const { loading, playing, muted, broken, started, noSound } = this.state;

    // Trick to force iOS start loading data
    const autoplay = __CLIENT__ ? !!/iPad|iPhone|iPod/.test(navigator.userAgent) : false;

    const cntSizeStyle = css({
      width:  fluid ? '100%' : width,
      height: fluid ? '100%' : height,
    });

    const buttonVisibilityStyle = playing ?
      css({ opacity: 0 }) :
      css({
        opacity: 0.6,
        ':hover': {
          opacity: 1
        },
        ':active': {
          opacity: 0.9
        },
        '@media (max-width: 701px)': {
          opacity: 1,
          ':hover':  { opacity: 1 },
          ':active': { opacity: 1 }
        }
      });

    const posterOpacityStyle = css({
      opacity: loading ? 0.8 : playing || started ? 0 : 1
    });

    return (
      <div {...merge(videoCntStyle, cntSizeStyle) }>

        { // loader
          loading ? <div { ...videoSpinnerStyle } ><VideoLoader /></div> : null
        }

        { // play button
          loading
          ? null
          :
            broken
            ? <div data-egg="Video killed the radio star..." { ...merge(playButtonStyle, fullVisibilityStyle) }>
                <VerticallyCentered>
                  <VideoBroken { ...toggleIconStyle }/>
                </VerticallyCentered>
              </div>
            : <button onClick={ this.togglePlaying } { ...merge(playButtonStyle, buttonVisibilityStyle) }>
                <VerticallyCentered>
                  <VideoPlay { ...toggleIconStyle } />
                </VerticallyCentered>
              </button>
        }

        { // sound button
          broken || loading
          ? null
          : <button
              /* className={ noSound ? null : 'video-volume-btn' } */
              className="video-volume-btn"
              onClick={ noSound ? null : this.toggleSound }
              title={ noSound ? null : muted ? 'Unmute sound' : 'Mute sound' }
              { ...merge(soundToggleButtonStyle, soundVisibilityStyle) }>
              {
                muted || noSound ?
                <VideoVolumeOff /> :
                <VideoVolumeOn />
              }
            </button>
        }

        { // no sound indication
          noSound && !loading && !broken
          ? <div
              className="video-no-sound"
              { ...videoNoSoundWrapperStyle }>
              <span { ...videoNoSoundTextStyle }>Video has no sound</span>
            </div>
          : null
        }

        { // video thumbnail
          broken
          ? null
          : <div { ...merge(posterStyle, posterOpacityStyle) } ><PosterImage url={ thumbnailURL } /></div>
        }
        {
          broken
          ? null
          : <video
              playsInline
              preload="auto"
              autoPlay={ autoplay }
              poster={ thumbnailURL }
              ref={ (ref) => { this.video = ref; } }
              onPlaying={ this.handlePlaying }
              onPause={ this.handlePause }
              onVolumeChange={ this.handleVolumeChange }
              onError={ this.handleError }
              onSuspend={ this.handleSuspend }
              onAbort={ this.handleAborted }
              onEmptied={ this.handleEmptied }
              onStalled={ this.handleStalled }
              onCanPlay={ this.handleCanPlay }
              onLoadStart={ this.handleLoadStart }
              onLoadedData={ this.handleLoadedData }
              onWaiting={ this.handleWaiting }
              onSeeking={ this.handleSeeking }
              onSeeked={ this.handleSeeked }
              { ...videoStyle }
            >
              {
                this.video ? <HLSSource
                isVideoChild
                src={ videoURL }
                video={ this.video }
                onSourceParsed={ this.handleSourceParsed }
                onSourceError={ this.handleSourceError }
              /> : null
            }
            </video>
        }
      </div>
    );
  }
}

const mapStateToProps = ({ media }) => ({ media });

const mapDispatchToProps = (dispatch) => ({});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(VideoPlayer);



// WEBPACK FOOTER //
// ../src/client/components/videos/VideoPlayer.js